import React, { useState, useRef, useCallback } from 'react';
// import "../../../styles/FileUploader.css";

import { GoTrash, GoCheckCircle } from "react-icons/go";
import { FiUploadCloud } from "react-icons/fi";
import { AiOutlineFilePdf, AiOutlineFileExcel, AiOutlineFileWord, AiOutlineFileZip, AiOutlineFileImage, AiOutlineFile } from "react-icons/ai";

// import { UploadIcon, TrashIcon, CheckIcon } from "../quote_portal/components/other/Icons";


const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileIcon = (type) => {
  if (type.startsWith("image/")) return <AiOutlineFileImage />;
  if (type === "application/pdf") return <AiOutlineFilePdf />;
  if (type.includes("spreadsheet") || type.includes("excel")) return <AiOutlineFileExcel />;
  if (type.includes("word") || type.includes("document")) return <AiOutlineFileWord />;
  if (type.includes("zip") || type.includes("compressed")) return <AiOutlineFileZip />;
  return <AiOutlineFile />;
  // if (type.startsWith("image/")) return "🖼️";
  // if (type === "application/pdf") return "📄";
  // if (type.includes("spreadsheet") || type.includes("excel")) return "📊";
  // if (type.includes("word") || type.includes("document")) return "📝";
  // if (type.includes("zip") || type.includes("compressed")) return "🗜️";
  return "📁";
};


export default function FileUploader({
  onFileSelect,
  accept = "application/pdf,image/jpeg,image/jpg,image/png,image/webp,image/gif",
  maxSizeMB = 10,
  multiple = false,
  maxFiles = 10,
}) {
  const handleSelect = (value) => {
    onFileSelect?.(value ?? null);
  };
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle | uploading | done
  const inputRef = useRef(null);

  const ALLOWED_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
  ];

  const validateFile = (f) => {
    if (!f) return { ok: false, error: "" };
    if (!ALLOWED_TYPES.includes(f.type)) {
      return { ok: false, error: "Only PDF and image files (JPG, PNG, WebP, GIF) are allowed." };
    }
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (f.size > maxBytes) {
      return { ok: false, error: `File is too large. Maximum size is ${maxSizeMB}MB.` };
    }
    return { ok: true, error: "" };
  };

  const validateAndSetFiles = (selectedFiles) => {
    setError("");
    const incoming = Array.from(selectedFiles || []).filter(Boolean);
    if (incoming.length === 0) return;

    const nextIncoming = incoming.slice(0, Math.max(0, maxFiles));

    for (const f of nextIncoming) {
      const { ok, error: errMsg } = validateFile(f);
      if (!ok) {
        setError(errMsg);
        return;
      }
    }

    setFiles((prev) => {
      const merged = multiple ? [...prev, ...nextIncoming] : [nextIncoming[0]];
      const capped = merged.slice(0, maxFiles);
      setUploadStatus("idle");
      setUploadProgress(0);
      handleSelect(multiple ? capped : capped[0] ?? null);
      simulateUpload();
      return capped;
    });
    setUploadStatus("idle");
    setUploadProgress(0);
  };

  const simulateUpload = () => {
    setUploadStatus("uploading");
    setUploadProgress(0);
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 18 + 5;
      if (progress >= 100) {
        clearInterval(interval);
        setUploadProgress(100);
        setUploadStatus("done");
      } else {
        setUploadProgress(Math.round(progress));
      }
    }, 120);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSetFiles(e.dataTransfer.files);
  }, [maxFiles, maxSizeMB, multiple]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = (e) => {
    validateAndSetFiles(e.target.files);
    e.target.value = "";
  };

  const handleRemoveAll = () => {
    setFiles([]);
    setUploadStatus("idle");
    setUploadProgress(0);
    setError("");
    handleSelect(null);
  };

  const handleRemoveOne = (idx) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      handleSelect(multiple ? next : next[0] ?? null);
      return next;
    });
  };

  const dropZoneClass = [
    "fu-drop-zone",
    isDragging && "fu-drop-zone--dragging",
    error && "fu-drop-zone--error",
    (!multiple && files.length > 0) && "fu-drop-zone--disabled",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="fu-wrapper">
      {/* Drop Zone */}
      <div
        className={dropZoneClass}
        onClick={() => {
          if (!multiple && files.length > 0) return;
          inputRef.current.click();
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          className="fu-hidden-input"
        />

        <div className="fu-upload-icon-wrap">
          <div className={`fu-upload-icon-bg${isDragging ? " fu-upload-icon-bg--active" : ""}`}>
            {/* <UploadIcon /> */}
            <FiUploadCloud />
          </div>
        </div>

        <div className="fu-drop-text">
          <span className="fu-click-text">Click to upload</span>
          <span className="fu-or-text"> or drag and drop</span>
        </div>
        <p className="fu-hint">
          PDF or image (JPG, PNG, WebP) up to {maxSizeMB}MB
          {multiple ? ` (up to ${maxFiles} files)` : ""}
        </p>
      </div>

      {/* Error */}
      {error && <p className="fu-error-text">{error}</p>}

      {/* File Cards */}
      {files.length > 0 && (
        <>
          {files.map((file, idx) => (
            <div className="fu-file-card" key={`${file.name}-${file.size}-${idx}`}>
              <div className="fu-file-icon-wrap">
                <span className="fu-file-emoji">{getFileIcon(file.type)}</span>
              </div>

              <div className="fu-file-info">
                <div className="fu-file-name-row">
                  <span className="fu-file-name" title={file.name}>
                    {file.name.length > 36 ? file.name.slice(0, 33) + "..." : file.name}
                  </span>
                  {uploadStatus === "done" && (
                    <span className="fu-check-badge">
                      {/* <CheckIcon /> */}
                      <GoCheckCircle />
                    </span>
                  )}
                </div>

                {uploadStatus !== "done" ? (
                  <>
                    <span className="fu-file-size">{formatFileSize(file.size)}</span>
                    <div className="fu-progress-track">
                      <div
                        className="fu-progress-bar"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span className="fu-progress-label">{uploadProgress}%</span>
                  </>
                ) : (
                  <span className="fu-file-size">{formatFileSize(file.size)}</span>
                )}
              </div>

              <button
                className="fu-remove-btn"
                onClick={() => (multiple ? handleRemoveOne(idx) : handleRemoveAll())}
                title="Remove file"
              >
                <GoTrash />
                {/* <TrashIcon /> */}
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
